import { WorkAvailabilityTag_userProfile$key } from '#app/__generated__/relay/WorkAvailabilityTag_userProfile.graphql.js';
import { Text } from '#app/components/Primitives/Text.js';
import { useURLParameters } from '#app/hooks/useURLParameters.js';
import { AlertTriangleIcon, ChevronRightIcon } from '@contra/icons/icons';
import { graphql, useFragment } from '@contra/relay';
import { css, styled } from 'styled-components';

const TagContainer = styled.div<{
  $isCurrentlyAvailable: boolean;
  $readOnly: boolean;
  $removeShadow: boolean;
}>`
  ${({ $isCurrentlyAvailable, $readOnly, $removeShadow, theme }) => css`
    display: flex;
    align-items: center;
    justify-content: center;

    width: fit-content;
    height: fit-content;

    padding: 6px 8px;
    border-radius: 6px;
    gap: 4px;
    box-shadow: ${$removeShadow ? '' : theme.boxShadowV2.basic20};

    ${$readOnly
      ? css`
          cursor: default;
        `
      : css`
          cursor: pointer;
        `}

    transition: background-color 0.2s ease-out;

    ${$isCurrentlyAvailable
      ? css`
          background-color: ${theme.colorsV2.green10};
          color: ${theme.colorsV2.green70};

          ${$readOnly
            ? undefined
            : css`
                &:hover {
                  background-color: ${theme.colorsV2.green20};
                }
              `}
        `
      : css`
          background-color: ${theme.colorsV2.yellow10};
          color: ${theme.colorsV2.yellow70};

          ${$readOnly
            ? undefined
            : css`
                &:hover {
                  background-color: ${theme.colorsV2.yellow20};
                }
              `}
        `}
  `}
`;

const TagText = styled(Text)`
  font-size: 13px;
  line-height: 13px;
  letter-spacing: 0.2px;
`;

const StyledAlertIcon = styled(AlertTriangleIcon)`
  color: ${({ theme }) => theme.colorsV2.yellow70};
`;

export const WorkAvailabilityTag = ({
  hideCaret = false,
  userProfileRef,
}: {
  readonly hideCaret?: boolean;
  readonly userProfileRef: WorkAvailabilityTag_userProfile$key;
}) => {
  const [, setURLParameters] = useURLParameters('/@username');
  const userProfile = useFragment<WorkAvailabilityTag_userProfile$key>(
    graphql`
      fragment WorkAvailabilityTag_userProfile on UserProfile {
        visitorCanEdit
        workPreferences {
          isCurrentlyAvailable
        }
      }
    `,
    userProfileRef,
  );

  const {
    visitorCanEdit,
    workPreferences: { isCurrentlyAvailable },
  } = userProfile;

  const label = isCurrentlyAvailable ? 'Available' : 'Unavailable';

  return (
    <TagContainer
      $isCurrentlyAvailable={isCurrentlyAvailable}
      $readOnly={!visitorCanEdit || hideCaret}
      $removeShadow={hideCaret}
      aria-label={label}
      onClick={() => {
        if (visitorCanEdit && !hideCaret) {
          setURLParameters({
            updateAvailability: true,
          });
        }
      }}
    >
      {visitorCanEdit && !isCurrentlyAvailable ? (
        <StyledAlertIcon size={16} />
      ) : null}
      <TagText textStyle="overlineBold">{label}</TagText>
      {visitorCanEdit && !hideCaret ? <ChevronRightIcon size={16} /> : null}
    </TagContainer>
  );
};
